import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Navigate, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Building2,
  Check,
  ChevronRight,
  Copy,
  FlaskConical,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  ReceiptText,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  Store,
  UserPlus,
  UserRound,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import {
  api,
  ApiRequestError,
  type PlatformAdmin,
  type PlatformAuditLog,
  type PlatformAuditResponse,
  type PlatformOverviewResponse,
  type PlatformTenant,
  type PlatformTenantsResponse,
} from "../lib/api";
import { formatDateTime, formatRupiah, initials } from "../lib/format";

const navigation = [
  {
    label: "Platform",
    items: [
      { to: "/platform", label: "Ringkasan", icon: LayoutDashboard, end: true },
      { to: "/platform/tenants", label: "Owner & Tenant", icon: Building2 },
    ],
  },
  {
    label: "Operasional",
    items: [
      { to: "/platform/billing", label: "Billing", icon: Wallet },
      { to: "/platform/audit", label: "Audit", icon: ScrollText },
    ],
  },
  {
    label: "Sistem",
    items: [
      {
        to: "/platform/settings",
        label: "Pengaturan & integrasi",
        icon: Settings,
      },
      { to: "/platform/profile", label: "Profil", icon: UserRound },
    ],
  },
];

const pageTitles: Record<string, string> = {
  "/platform": "Ringkasan",
  "/platform/tenants": "Owner & Tenant",
  "/platform/billing": "Billing",
  "/platform/audit": "Audit",
  "/platform/settings": "Pengaturan & integrasi",
  "/platform/profile": "Profil",
};

type TenantFilter = "all" | "active" | "attention";
type HealthTone = "good" | "warn" | "danger";

type PlatformBillingConfig = {
  publicId: string;
  apiBaseUrl: string;
  hasApiToken: boolean;
  hasWebhookSecret: boolean;
  webhookUrl: string;
};

type PlatformWaConfig = {
  apiBaseUrl: string;
  hasApiKey: boolean;
  source: "settings" | "environment";
};

type PlatformBillingResponse = {
  webqris: PlatformBillingConfig | null;
  wa: PlatformWaConfig | null;
  summary: {
    depositTotal: number;
    feeTotal: number;
    topupTotal: number;
    adjustmentTotal: number;
  };
};

type PlatformLedgerEntry = {
  id: number;
  entryType: string;
  direction: string;
  amount: number;
  balanceAfter: number;
  referenceType: string | null;
  referenceId: string | null;
  note: string | null;
  createdAt: string;
};

type PlatformLedgerResponse = {
  balance: number;
  entries: PlatformLedgerEntry[];
  topups: Array<{
    id: string;
    amount: number;
    status: string;
    createdAt: string;
    paidAt: string | null;
  }>;
};

export function PlatformRoot() {
  const [admin, setAdmin] = useState<PlatformAdmin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const response = await api<{ admin: PlatformAdmin }>(
          "/api/platform/auth/me",
        );
        if (active) setAdmin(response.admin);
      } catch {
        if (active) setAdmin(null);
      } finally {
        if (active) setLoading(false);
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await api("/api/platform/auth/logout", { method: "POST" });
    } finally {
      setAdmin(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="boot-screen">
        <BrandMark size="lg" />
        <span className="boot-screen__name">Platform Console</span>
        <span className="boot-screen__line" />
        <span className="boot-screen__hint">Memeriksa sesi superadmin...</span>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <PlatformConsole
      admin={admin}
      onLogout={handleLogout}
      onAdminUpdated={setAdmin}
    />
  );
}

function PlatformConsole({
  admin,
  onLogout,
  onAdminUpdated,
}: {
  admin: PlatformAdmin;
  onLogout: () => Promise<void>;
  onAdminUpdated: (admin: PlatformAdmin) => void;
}) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [overview, setOverview] = useState<PlatformOverviewResponse | null>(
    null,
  );
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<PlatformTenant | null>(
    null,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [billing, setBilling] = useState<PlatformBillingResponse | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [overviewResponse, tenantsResponse, billingResponse] = await Promise.all([
        api<PlatformOverviewResponse>("/api/platform/overview"),
        api<PlatformTenantsResponse>("/api/platform/tenants"),
        api<PlatformBillingResponse>("/api/platform/billing"),
      ]);
      setOverview(overviewResponse);
      setTenants(tenantsResponse.tenants);
      setBilling(billingResponse);
      setError(null);
    } catch (requestError) {
      if (
        requestError instanceof ApiRequestError &&
        requestError.status === 401
      ) {
        await onLogout();
        return;
      }
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat data platform.",
      );
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSelectedTenant(null);
  }, [location.pathname]);

  const activeTenant = selectedTenant
    ? tenants.find((tenant) => tenant.id === selectedTenant.id) ?? selectedTenant
    : null;
  const title = pageTitles[location.pathname] ?? "Platform";
  const today = new Intl.DateTimeFormat("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());

  const tab = location.pathname.startsWith("/platform/tenants")
    ? "tenants"
    : location.pathname.startsWith("/platform/billing")
      ? "billing"
      : location.pathname.startsWith("/platform/audit")
        ? "audit"
        : location.pathname.startsWith("/platform/settings")
          ? "settings"
          : location.pathname.startsWith("/platform/profile")
            ? "profile"
            : "overview";

  return (
    <div className="app-shell">
      {mobileOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Tutup navigasi"
        />
      ) : null}

      <aside className={`sidebar${mobileOpen ? " is-open" : ""}`}>
        <div className="sidebar__brand">
          <div className="brand-lockup brand-lockup--sidebar">
            <BrandMark />
            <div>
              <strong>Cafe System Control</strong>
              <span>Platform Console</span>
            </div>
          </div>
          <button
            type="button"
            className="sidebar__close"
            onClick={() => setMobileOpen(false)}
            aria-label="Tutup navigasi"
          >
            <X size={18} />
          </button>
        </div>

        <div className="sidebar__tenant sidebar__tenant--platform">
          <div className="sidebar__tenant-avatar">
            <ShieldCheck size={17} />
          </div>
          <div>
            <strong>Platform control</strong>
            <span>
              <FlaskConical size={12} /> Superadmin realm
            </span>
          </div>
        </div>

        <nav className="sidebar__nav">
          {navigation.map((group) => (
            <div className="nav-group" key={group.label}>
              <p className="nav-group__label">{group.label}</p>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `nav-link${isActive ? " is-active" : ""}`
                    }
                    onClick={() => setMobileOpen(false)}
                  >
                    <Icon size={17} />
                    <span>{item.label}</span>
                    <ChevronRight className="nav-link__chevron" size={14} />
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="user-chip">
            <span className="avatar">{initials(admin.name)}</span>
            <div>
              <strong>{admin.name}</strong>
              <span>@{admin.username}</span>
            </div>
          </div>
          <button
            type="button"
            className="icon-button icon-button--dark"
            onClick={() => void onLogout()}
            aria-label="Keluar"
            title="Keluar"
          >
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div className="topbar__left">
            <button
              type="button"
              className="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              aria-label="Buka navigasi"
            >
              <Menu size={20} />
            </button>
            <Link
              to="/platform"
              className="topbar-home"
              aria-label="Kembali ke ringkasan platform"
              title="Kembali ke ringkasan platform"
            >
              <LayoutDashboard size={16} />
              <span>Platform</span>
            </Link>
            <Link
              to="/platform"
              className="topbar__brand"
              aria-label="Cafe System Control — ringkasan platform"
              title="Kembali ke ringkasan platform"
            >
              <BrandMark size="sm" />
            </Link>
            <div className="breadcrumb">
              <Link to="/platform">Platform</Link>
              <ChevronRight size={13} />
              <strong>{title}</strong>
            </div>
          </div>

          <div className="topbar__right">
            <span className="superadmin-pill">
              <ShieldCheck size={13} />
              Superadmin
            </span>
            <span className="topbar__date">{today}</span>
            <Link
              to="/platform/profile"
              className="topbar__account"
              aria-label={`Buka profil ${admin.name}`}
              title={`${admin.name} · @${admin.username} · Superadmin`}
            >
              <span className="avatar avatar--small">
                {initials(admin.name)}
              </span>
              <span className="topbar__account-copy">
                <strong>{admin.name}</strong>
                <small>@{admin.username} · Superadmin</small>
              </span>
            </Link>
          </div>
        </header>

        <main className="content">
          {error ? (
            <div className="alert alert--danger" role="alert">
              <ShieldCheck size={17} />
              <span>{error}</span>
            </div>
          ) : null}
          {notice ? (
            <div className="alert alert--success" role="status">
              <Check size={17} />
              <span>{notice}</span>
            </div>
          ) : null}

          {loading ? (
            <PlatformLoading />
          ) : tab === "overview" ? (
            <PlatformOverviewPage
              overview={overview}
              tenants={tenants}
              onRefresh={() => void load()}
              onSelect={setSelectedTenant}
            />
          ) : tab === "tenants" ? (
            <PlatformTenantsPage
              tenants={tenants}
              onRefresh={() => void load()}
              onSelect={setSelectedTenant}
              onCreate={() => setCreateOpen(true)}
            />
          ) : tab === "billing" ? (
            <PlatformBillingPage
              tenants={tenants}
              overview={overview}
              billing={billing}
              onRefresh={() => void load()}
              onSelect={setSelectedTenant}
            />
          ) : tab === "audit" ? (
            <PlatformAuditPage onLogout={onLogout} />
          ) : tab === "settings" ? (
            <PlatformSettingsPage
              overview={overview}
              tenants={tenants}
              billing={billing}
              onRefresh={() => void load()}
            />
          ) : tab === "profile" ? (
            <PlatformProfilePage admin={admin} onUpdated={onAdminUpdated} />
          ) : (
            <PlatformPlaceholder tab={tab} />
          )}
        </main>
      </div>

      {activeTenant ? (
        <TenantDetailDrawer
          tenant={activeTenant}
          onClose={() => setSelectedTenant(null)}
          onRefresh={() => void load()}
        />
      ) : null}

      {createOpen ? (
        <CreateOwnerSheet
          onClose={() => setCreateOpen(false)}
          onCreated={(name) => {
            setCreateOpen(false);
            setNotice(`Owner dan tenant ${name} berhasil dibuat.`);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

function PlatformLoading() {
  return (
    <div className="page-stack">
      <div>
        <span className="skeleton skeleton--title" />
        <span className="skeleton skeleton--line" />
      </div>
      <div className="stat-grid">
        {[0, 1, 2, 3].map((index) => (
          <div className="card card--pad" key={index}>
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--value" />
          </div>
        ))}
      </div>
      <div className="card card--pad">
        <span className="skeleton skeleton--block" />
      </div>
    </div>
  );
}

function PlatformOverviewPage({
  overview,
  tenants,
  onRefresh,
  onSelect,
}: {
  overview: PlatformOverviewResponse | null;
  tenants: PlatformTenant[];
  onRefresh: () => void;
  onSelect: (tenant: PlatformTenant) => void;
}) {
  const counts = overview?.counts;
  const recent = overview?.recentTenants ?? [];

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Platform"
        title="Ringkasan platform"
        description="Kesehatan tenancy, operasional, dan pertumbuhan platform."
        actions={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onRefresh}
          >
            Muat ulang
          </button>
        }
      />

      <section className="stat-grid" aria-label="Ringkasan platform">
        <article className="stat-card">
          <span className="stat-card__icon stat-card__icon--accent">
            <Building2 size={18} />
          </span>
          <div>
            <p className="stat-card__label">Tenant</p>
            <strong className="stat-card__value">{counts?.tenants ?? 0}</strong>
            <span className="stat-card__note">
              {counts?.activeTenants ?? 0} aktif
            </span>
          </div>
        </article>

        <article className="stat-card">
          <span className="stat-card__icon">
            <Store size={18} />
          </span>
          <div>
            <p className="stat-card__label">Outlet</p>
            <strong className="stat-card__value">{counts?.outlets ?? 0}</strong>
            <span className="stat-card__note">
              {counts?.units ?? 0} meja/kamar
            </span>
          </div>
        </article>

        <article className="stat-card">
          <span className="stat-card__icon">
            <ReceiptText size={18} />
          </span>
          <div>
            <p className="stat-card__label">Pesanan hari ini</p>
            <strong className="stat-card__value">
              {counts?.ordersToday ?? 0}
            </strong>
            <span className="stat-card__note">Semua tenant</span>
          </div>
        </article>

        <article className="stat-card">
          <span className="stat-card__icon">
            <Wallet size={18} />
          </span>
          <div>
            <p className="stat-card__label">Omzet hari ini</p>
            <strong className="stat-card__value">
              {formatRupiah(counts?.revenueToday ?? 0)}
            </strong>
            <span className="stat-card__note">Pesanan sudah dibayar</span>
          </div>
        </article>
      </section>

      <div className="overview-grid">
        <section className="card card--pad">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Tenancy</p>
              <h2>Tenant terbaru</h2>
              <p className="section-subtitle">
                {counts?.tenants ?? 0} tenant terdaftar di platform.
              </p>
            </div>
            <Link className="text-link" to="/platform/tenants">
              Lihat semua <ArrowRight size={14} />
            </Link>
          </div>

          {recent.length > 0 ? (
            <>
              <div className="tenant-table-desktop">
                <TenantTable tenants={recent} onSelect={onSelect} />
              </div>
              <TenantCardList tenants={recent} onSelect={onSelect} />
            </>
          ) : (
            <div className="empty-state">
              <span className="empty-state__icon">
                <Building2 size={24} />
              </span>
              <h3>Belum ada tenant</h3>
              <p>Tenant akan muncul setelah perusahaan pertama didaftarkan.</p>
            </div>
          )}
        </section>

        <aside className="card card--pad overview-side">
          <div className="section-heading section-heading--compact">
            <div>
              <p className="eyebrow">Realm</p>
              <h2>Akses & keamanan</h2>
            </div>
            <ShieldCheck size={18} className="muted-icon" />
          </div>

          <div className="platform-realm">
            <div className="platform-realm__row">
              <span>Realm superadmin</span>
              <strong>Terpisah</strong>
            </div>
            <div className="platform-realm__row">
              <span>Akun owner terlihat</span>
              <strong>Tidak</strong>
            </div>
            <div className="platform-realm__row">
              <span>Session owner</span>
              <strong>Realm berbeda</strong>
            </div>
            <div className="platform-realm__row">
              <span>Audit login</span>
              <strong>Aktif</strong>
            </div>
          </div>

          <p className="fine-print">
            Superadmin tidak muncul di daftar user tenant. Setiap aksi platform
            dicatat dengan actor type <code>platform_admin</code>.
          </p>
        </aside>
      </div>

      <PlatformAttention tenants={tenants} onSelect={onSelect} />

      <section className="card card--pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Kapasitas</p>
            <h2>Jejak operasional</h2>
          </div>
        </div>
        <div className="platform-metrics">
          <div>
            <span>Menu aktif</span>
            <strong>{counts?.menuItems ?? 0}</strong>
          </div>
          <div>
            <span>Unit meja/kamar</span>
            <strong>{counts?.units ?? 0}</strong>
          </div>
          <div>
            <span>Outlet</span>
            <strong>{counts?.outlets ?? 0}</strong>
          </div>
          <div>
            <span>Tenant aktif</span>
            <strong>{counts?.activeTenants ?? 0}</strong>
          </div>
        </div>
      </section>
    </div>
  );
}

function PlatformAttention({
  tenants,
  onSelect,
}: {
  tenants: PlatformTenant[];
  onSelect: (tenant: PlatformTenant) => void;
}) {
  const attention = tenants.filter(needsAttention);
  const visible = attention.slice(0, 4);

  return (
    <section className="card card--pad">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Tindak lanjut</p>
          <h2>Perlu perhatian</h2>
          <p className="section-subtitle">
            Tenant nonaktif atau integrasi intinya belum lengkap.
          </p>
        </div>
        <span className="progress-pill">{attention.length} tenant</span>
      </div>

      {visible.length > 0 ? (
        <div className="attention-list">
          {visible.map((tenant) => {
            const health = tenantHealth(tenant);
            return (
              <button
                type="button"
                className="attention-item"
                key={tenant.id}
                onClick={() => onSelect(tenant)}
              >
                <span
                  className={`attention-item__icon attention-item__icon--${health.tone}`}
                >
                  <AlertTriangle size={16} />
                </span>
                <span className="attention-item__body">
                  <strong>{tenant.name}</strong>
                  <span>{health.detail}</span>
                </span>
                <StatusBadge status={tenant.status} />
                <ChevronRight size={15} className="attention-item__chevron" />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="empty-state empty-state--compact">
          <span className="empty-state__icon">
            <BadgeCheck size={22} />
          </span>
          <h3>Semua tenant sehat</h3>
          <p>Status tenant dan integrasi inti sudah lengkap.</p>
        </div>
      )}

      {attention.length > visible.length ? (
        <p className="fine-print">
          {attention.length - visible.length} tenant lain juga perlu ditinjau.
        </p>
      ) : null}
    </section>
  );
}

function PlatformTenantsPage({
  tenants,
  onRefresh,
  onSelect,
  onCreate,
}: {
  tenants: PlatformTenant[];
  onRefresh: () => void;
  onSelect: (tenant: PlatformTenant) => void;
  onCreate: () => void;
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<TenantFilter>("all");
  const query = search.trim().toLowerCase();

  const filtered = tenants.filter((tenant) => {
    const matchesSearch =
      !query ||
      [
        tenant.name,
        tenant.slug,
        tenant.owner_name ?? "",
        tenant.owner_username ?? "",
        tenant.owner_email ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);

    const matchesFilter =
      filter === "all" ||
      (filter === "active" && tenant.status === "active") ||
      (filter === "attention" && needsAttention(tenant));

    return matchesSearch && matchesFilter;
  });

  const filterCounts: Record<TenantFilter, number> = {
    all: tenants.length,
    active: tenants.filter((tenant) => tenant.status === "active").length,
    attention: tenants.filter(needsAttention).length,
  };

  const filters: Array<{ value: TenantFilter; label: string }> = [
    { value: "all", label: "Semua" },
    { value: "active", label: "Aktif" },
    { value: "attention", label: "Perlu perhatian" },
  ];

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Platform"
        title="Owner & Tenant"
        description="Semua perusahaan yang memakai platform, beserta status integrasinya."
        actions={
          <div className="card-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onRefresh}
            >
              Muat ulang
            </button>
            <button type="button" className="btn btn-primary" onClick={onCreate}>
              <UserPlus size={16} /> Tambah owner
            </button>
          </div>
        }
      />

      <section className="card card--pad">
        <div className="tenant-filters" role="group" aria-label="Filter tenant">
          {filters.map((item) => (
            <button
              type="button"
              key={item.value}
              className={`filter-chip${filter === item.value ? " is-active" : ""}`}
              onClick={() => setFilter(item.value)}
            >
              {item.label}
              <span>{filterCounts[item.value]}</span>
            </button>
          ))}
        </div>

        <div className="table-toolbar">
          <label className="search-field">
            <Search size={16} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cari nama, slug, atau owner..."
              aria-label="Cari tenant"
            />
          </label>
          <span className="table-count">
            {filtered.length} dari {tenants.length} tenant
          </span>
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="tenant-table-desktop">
              <TenantTable tenants={filtered} onSelect={onSelect} />
            </div>
            <TenantCardList tenants={filtered} onSelect={onSelect} />
          </>
        ) : (
          <div className="empty-state">
            <span className="empty-state__icon">
              <Users size={24} />
            </span>
            <h3>Tidak ada tenant yang cocok</h3>
            <p>Coba kata kunci lain atau muat ulang data.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function TenantTable({
  tenants,
  onSelect,
}: {
  tenants: PlatformTenant[];
  onSelect: (tenant: PlatformTenant) => void;
}) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Tenant</th>
            <th>Owner</th>
            <th>Status</th>
            <th>Operasional</th>
            <th>Integrasi</th>
            <th>Dibuat</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((tenant) => {
            const health = tenantHealth(tenant);
            return (
              <tr
                key={tenant.id}
                className="data-table__row--clickable"
                tabIndex={0}
                onClick={() => onSelect(tenant)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(tenant);
                  }
                }}
                aria-label={`Lihat detail ${tenant.name}`}
              >
                <td>
                  <div className="tenant-cell">
                    <span className="tenant-cell__logo">
                      {initials(tenant.name)}
                    </span>
                    <div>
                      <strong>{tenant.name}</strong>
                      <span>/{tenant.slug}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="owner-cell">
                    <strong>{tenant.owner_name ?? "Belum ada owner"}</strong>
                    <span>
                      {tenant.owner_username
                        ? `@${tenant.owner_username}`
                        : tenant.owner_email ?? "-"}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="status-cell">
                    <StatusBadge status={tenant.status} />
                    <span
                      className={`tenant-health tenant-health--${health.tone}`}
                      title={health.detail}
                    >
                      {health.label}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="ops-cell">
                    <span>{tenant.outlets} outlet</span>
                    <span>
                      {tenant.units} unit · {tenant.menu_items} menu
                    </span>
                  </div>
                </td>
                <td>
                  <div className="integration-cell">
                    <StatusBadge
                      status={tenant.webqris_status ?? "not_configured"}
                      label={
                        tenant.webqris_status === "active"
                          ? "QRIS aktif"
                          : "QRIS belum"
                      }
                    />
                    <StatusBadge
                      status={tenant.wa_status ?? "not_configured"}
                      label={
                        tenant.wa_status === "active" ? "WA aktif" : "WA belum"
                      }
                    />
                  </div>
                </td>
                <td className="muted-cell">
                  {formatDateTime(tenant.created_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TenantCardList({
  tenants,
  onSelect,
}: {
  tenants: PlatformTenant[];
  onSelect: (tenant: PlatformTenant) => void;
}) {
  return (
    <div className="tenant-card-list">
      {tenants.map((tenant) => {
        const health = tenantHealth(tenant);
        return (
          <button
            type="button"
            className="tenant-card"
            key={tenant.id}
            onClick={() => onSelect(tenant)}
          >
            <div className="tenant-card__top">
              <div className="tenant-cell">
                <span className="tenant-cell__logo">
                  {initials(tenant.name)}
                </span>
                <div>
                  <strong>{tenant.name}</strong>
                  <span>/{tenant.slug}</span>
                </div>
              </div>
              <StatusBadge status={tenant.status} />
            </div>

            <span className={`tenant-health tenant-health--${health.tone}`}>
              {health.label}
            </span>

            <div className="tenant-card__owner">
              <span>Owner</span>
              <strong>{tenant.owner_name ?? "Belum ada owner"}</strong>
              <small>
                {tenant.owner_username
                  ? `@${tenant.owner_username}`
                  : tenant.owner_email ?? "-"}
              </small>
            </div>

            <div className="tenant-card__stats">
              <span>
                <strong>{tenant.outlets}</strong> outlet
              </span>
              <span>
                <strong>{tenant.units}</strong> unit
              </span>
              <span>
                <strong>{tenant.menu_items}</strong> menu
              </span>
            </div>

            <div className="tenant-card__integrations">
              <StatusBadge
                status={tenant.webqris_status ?? "not_configured"}
                label={
                  tenant.webqris_status === "active"
                    ? "QRIS aktif"
                    : "QRIS belum"
                }
              />
              <StatusBadge
                status={tenant.wa_status ?? "not_configured"}
                label={tenant.wa_status === "active" ? "WA aktif" : "WA belum"}
              />
            </div>

            <span className="tenant-card__cta">
              Lihat detail <ChevronRight size={15} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function tenantHealth(tenant: PlatformTenant): {
  tone: HealthTone;
  label: string;
  detail: string;
} {
  if (
    tenant.status === "suspended" ||
    tenant.status === "expired" ||
    tenant.status === "cancelled"
  ) {
    return {
      tone: "danger",
      label: "Perlu tindakan",
      detail: `Status tenant: ${tenant.status}`,
    };
  }

  const missing: string[] = [];
  if (tenant.webqris_status !== "active") missing.push("WebQRIS");
  if (tenant.wa_status !== "active") missing.push("WhatsApp");

  if (missing.length > 0) {
    return {
      tone: "warn",
      label: "Setup belum lengkap",
      detail: `${missing.join(" dan ")} belum aktif`,
    };
  }

  if (tenant.status === "grace_period") {
    return {
      tone: "warn",
      label: "Masa tenggang",
      detail: "Tenant perlu menyelesaikan pembayaran",
    };
  }

  return {
    tone: "good",
    label: "Sehat",
    detail: "Status aktif dan integrasi inti lengkap",
  };
}

function needsAttention(tenant: PlatformTenant): boolean {
  return tenantHealth(tenant).tone !== "good";
}

function billingLabel(value: string): string {
  if (value === "manual_free") return "Bebas manual";
  if (value === "internal") return "Internal";
  return "Berbayar";
}

function statusLabel(value: string): string {
  const labels: Record<string, string> = {
    trial: "Trial",
    active: "Aktif",
    grace_period: "Masa tenggang",
    suspended: "Suspend",
    expired: "Kedaluwarsa",
    cancelled: "Dibatalkan",
  };
  return labels[value] ?? value;
}

function feeModeLabel(value: string): string {
  const labels: Record<string, string> = {
    per_order: "Per order",
    subscription: "Subscription",
    hybrid: "Hybrid",
  };
  return labels[value] ?? value;
}

function TenantDetailDrawer({
  tenant,
  onClose,
  onRefresh,
}: {
  tenant: PlatformTenant;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState(tenant.status);
  const [billingType, setBillingType] = useState(tenant.billing_type);
  const [feeMode, setFeeMode] = useState(tenant.fee_mode ?? "per_order");
  const [feeAmount, setFeeAmount] = useState(
    String(tenant.fee_amount ?? 100),
  );
  const [feeActive, setFeeActive] = useState(tenant.fee_active ?? true);
  const [tenantName, setTenantName] = useState(tenant.name);
  const [ownerName, setOwnerName] = useState(tenant.owner_name ?? "");
  const [ownerUsername, setOwnerUsername] = useState(
    tenant.owner_username ?? "",
  );
  const [ownerPhone, setOwnerPhone] = useState(tenant.owner_phone ?? "");
  const [ownerEmail, setOwnerEmail] = useState(tenant.owner_email ?? "");
  const [ledger, setLedger] = useState<PlatformLedgerResponse | null>(null);
  const [depositAmount, setDepositAmount] = useState("50000");
  const [depositNote, setDepositNote] = useState("");
  const [saving, setSaving] = useState<
    | "status"
    | "billing"
    | "fee"
    | "tenant"
    | "owner"
    | "reset"
    | "deposit"
    | null
  >(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const health = tenantHealth(tenant);
  const feeChanged =
    tenant.fee_active === null ||
    feeMode !== (tenant.fee_mode ?? "per_order") ||
    Number(feeAmount) !== (tenant.fee_amount ?? 100) ||
    feeActive !== (tenant.fee_active ?? true);

  useEffect(() => {
    setStatus(tenant.status);
    setBillingType(tenant.billing_type);
    setFeeMode(tenant.fee_mode ?? "per_order");
    setFeeAmount(String(tenant.fee_amount ?? 100));
    setFeeActive(tenant.fee_active ?? true);
    setTenantName(tenant.name);
    setOwnerName(tenant.owner_name ?? "");
    setOwnerUsername(tenant.owner_username ?? "");
    setOwnerPhone(tenant.owner_phone ?? "");
    setOwnerEmail(tenant.owner_email ?? "");
    void loadLedger();
  }, [
    tenant.id,
    tenant.status,
    tenant.billing_type,
    tenant.fee_mode,
    tenant.fee_amount,
    tenant.fee_active,
    tenant.name,
    tenant.owner_name,
    tenant.owner_username,
    tenant.owner_phone,
    tenant.owner_email,
  ]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  async function copySlug() {
    try {
      await navigator.clipboard.writeText(tenant.slug);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  function requestErrorText(requestError: unknown): string {
    return requestError instanceof ApiRequestError
      ? requestError.message
      : "Tidak dapat menyimpan perubahan. Coba lagi.";
  }

  async function saveStatus() {
    if (status === tenant.status) return;
    if (
      ["suspended", "expired", "cancelled"].includes(status) &&
      !window.confirm(
        `Ubah status ${tenant.name} menjadi ${status}? Aksi ini tercatat di audit log.`,
      )
    ) {
      return;
    }

    setSaving("status");
    setMessage(null);
    setError(null);
    try {
      await api(`/api/platform/tenants/${tenant.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      setMessage("Status tenant diperbarui.");
      onRefresh();
    } catch (requestError) {
      setError(requestErrorText(requestError));
    } finally {
      setSaving(null);
    }
  }

  async function saveBilling() {
    if (billingType === tenant.billing_type) return;

    setSaving("billing");
    setMessage(null);
    setError(null);
    try {
      await api(`/api/platform/tenants/${tenant.id}/billing`, {
        method: "PATCH",
        body: JSON.stringify({ billing_type: billingType }),
      });
      setMessage("Tipe billing diperbarui.");
      onRefresh();
    } catch (requestError) {
      setError(requestErrorText(requestError));
    } finally {
      setSaving(null);
    }
  }

  async function saveFee() {
    const amount = Number(feeAmount);
    if (!Number.isInteger(amount) || amount < 0) {
      setError("Nominal fee harus bilangan bulat minimal 0.");
      return;
    }

    setSaving("fee");
    setMessage(null);
    setError(null);
    try {
      await api(`/api/platform/tenants/${tenant.id}/fee-rule`, {
        method: "PUT",
        body: JSON.stringify({
          mode: feeMode,
          amount,
          active: feeActive,
        }),
      });
      setMessage("Fee rule diperbarui.");
      onRefresh();
    } catch (requestError) {
      setError(requestErrorText(requestError));
    } finally {
      setSaving(null);
    }
  }

  async function saveTenantName() {
    if (tenantName.trim().length < 2 || tenantName.trim() === tenant.name) {
      return;
    }
    setSaving("tenant");
    setMessage(null);
    setError(null);
    try {
      await api(`/api/platform/tenants/${tenant.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: tenantName.trim() }),
      });
      setMessage("Nama tenant diperbarui.");
      onRefresh();
    } catch (requestError) {
      setError(requestErrorText(requestError));
    } finally {
      setSaving(null);
    }
  }

  async function saveOwner() {
    if (ownerName.trim().length < 2 || ownerUsername.trim().length < 3) {
      setError("Nama owner dan username wajib diisi.");
      return;
    }
    setSaving("owner");
    setMessage(null);
    setError(null);
    try {
      await api(`/api/platform/tenants/${tenant.id}/owner`, {
        method: "PATCH",
        body: JSON.stringify({
          name: ownerName.trim(),
          username: ownerUsername.trim(),
          phone: ownerPhone.trim() || null,
          email: ownerEmail.trim() || null,
        }),
      });
      setMessage("Data owner diperbarui.");
      onRefresh();
    } catch (requestError) {
      setError(requestErrorText(requestError));
    } finally {
      setSaving(null);
    }
  }

  async function loadLedger() {
    try {
      const response = await api<PlatformLedgerResponse>(
        `/api/platform/tenants/${tenant.id}/ledger`,
      );
      setLedger(response);
    } catch (requestError) {
      console.error("gagal memuat ledger tenant", requestError);
    }
  }

  async function adjustDeposit(direction: "credit" | "debit") {
    const amount = Number(depositAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      setError("Nominal deposit harus bilangan bulat positif.");
      return;
    }
    setSaving("deposit");
    setMessage(null);
    setError(null);
    try {
      const response = await api<{ balance: number }>(
        `/api/platform/tenants/${tenant.id}/deposit`,
        {
          method: "POST",
          body: JSON.stringify({
            entry_type: direction === "credit" ? "deposit" : "adjustment",
            direction,
            amount,
            note:
              depositNote.trim() ||
              (direction === "credit"
                ? "Top-up manual oleh superadmin"
                : "Koreksi saldo oleh superadmin"),
          }),
        },
      );
      setMessage(
        direction === "credit"
          ? `Deposit ditambahkan. Saldo ${formatRupiah(response.balance)}.`
          : `Saldo dipotong. Saldo ${formatRupiah(response.balance)}.`,
      );
      setDepositNote("");
      await loadLedger();
      onRefresh();
    } catch (requestError) {
      setError(requestErrorText(requestError));
    } finally {
      setSaving(null);
    }
  }

  async function sendOwnerPasswordReset() {
    setSaving("reset");
    setMessage(null);
    setError(null);
    try {
      const result = await api<{ sent: boolean; phone: string }>(
        `/api/platform/tenants/${tenant.id}/owner/password-reset`,
        { method: "POST" },
      );
      setMessage(`Kode reset password dikirim ke WhatsApp ${result.phone}.`);
    } catch (requestError) {
      setError(requestErrorText(requestError));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div
      className="platform-drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="platform-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tenant-detail-title"
      >
        <header className="platform-drawer__header">
          <div className="platform-drawer__identity">
            <span className="tenant-cell__logo tenant-cell__logo--large">
              {initials(tenant.name)}
            </span>
            <div>
              <p className="eyebrow">Tenant detail</p>
              <h2 id="tenant-detail-title">{tenant.name}</h2>
              <span className="platform-drawer__slug">/{tenant.slug}</span>
            </div>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            aria-label="Tutup detail tenant"
          >
            <X size={18} />
          </button>
        </header>

        <div className="platform-drawer__body">
          <div className={`tenant-health-banner tenant-health-banner--${health.tone}`}>
            {health.tone === "good" ? (
              <BadgeCheck size={18} />
            ) : (
              <AlertTriangle size={18} />
            )}
            <div>
              <strong>{health.label}</strong>
              <span>{health.detail}</span>
            </div>
          </div>

          {message ? (
            <div className="alert alert--success" role="status">
              <Check size={17} />
              <span>{message}</span>
            </div>
          ) : null}
          {error ? (
            <div className="alert alert--danger" role="alert">
              <AlertTriangle size={17} />
              <span>{error}</span>
            </div>
          ) : null}

          <section className="drawer-section">
            <p className="eyebrow">Owner</p>
            <div className="drawer-owner">
              <span className="avatar">
                {initials(tenant.owner_name ?? "Owner")}
              </span>
              <div>
                <strong>{tenant.owner_name ?? "Belum ada owner"}</strong>
                <span>
                  {tenant.owner_username
                    ? `@${tenant.owner_username}`
                    : tenant.owner_email ?? "Kontak belum tersedia"}
                </span>
              </div>
            </div>
          </section>

          <section className="drawer-section">
            <p className="eyebrow">Identitas</p>
            <div className="form-grid">
              <label className="field field--full">
                <span>Nama tenant</span>
                <span className="input-group">
                  <input
                    className="input"
                    value={tenantName}
                    onChange={(event) => setTenantName(event.target.value)}
                    maxLength={120}
                  />
                  <button
                    type="button"
                    className="input-group__action"
                    title="Simpan nama tenant"
                    onClick={() => void saveTenantName()}
                    disabled={
                      saving !== null ||
                      tenantName.trim().length < 2 ||
                      tenantName.trim() === tenant.name
                    }
                  >
                    {saving === "tenant" ? (
                      <span className="spin" />
                    ) : (
                      <Check size={16} />
                    )}
                  </button>
                </span>
              </label>
              <label className="field">
                <span>Nama owner</span>
                <input
                  className="input"
                  value={ownerName}
                  onChange={(event) => setOwnerName(event.target.value)}
                  maxLength={120}
                />
              </label>
              <label className="field">
                <span>Username owner</span>
                <input
                  className="input"
                  autoCapitalize="none"
                  value={ownerUsername}
                  onChange={(event) => setOwnerUsername(event.target.value)}
                  maxLength={32}
                />
              </label>
              <label className="field">
                <span>WhatsApp owner</span>
                <input
                  className="input"
                  inputMode="tel"
                  value={ownerPhone}
                  onChange={(event) => setOwnerPhone(event.target.value)}
                  placeholder="08xxxxxxxxxx"
                />
              </label>
              <label className="field">
                <span>Email owner</span>
                <input
                  className="input"
                  type="email"
                  value={ownerEmail}
                  onChange={(event) => setOwnerEmail(event.target.value)}
                  placeholder="opsional"
                />
              </label>
            </div>
            <div className="card-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void saveOwner()}
                disabled={saving !== null}
              >
                <Check size={16} /> Simpan owner
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void sendOwnerPasswordReset()}
                disabled={saving !== null || !tenant.owner_phone}
              >
                <KeyRound size={16} /> Kirim reset password
              </button>
            </div>
            <p className="fine-print">
              Reset password dikirim sebagai OTP ke WhatsApp owner. Superadmin
              tidak melihat password owner.
            </p>
          </section>

          <section className="drawer-section">
            <p className="eyebrow">Operasional</p>
            <div className="drawer-stat-grid">
              <div className="drawer-stat">
                <Store size={16} />
                <strong>{tenant.outlets}</strong>
                <span>Outlet</span>
              </div>
              <div className="drawer-stat">
                <Building2 size={16} />
                <strong>{tenant.units}</strong>
                <span>Meja/kamar</span>
              </div>
              <div className="drawer-stat">
                <ReceiptText size={16} />
                <strong>{tenant.menu_items}</strong>
                <span>Menu</span>
              </div>
            </div>
          </section>

          <section className="drawer-section">
            <p className="eyebrow">Integrasi</p>
            <div className="drawer-integrations">
              <div>
                <span>WebQRIS</span>
                <StatusBadge
                  status={tenant.webqris_status ?? "not_configured"}
                  label={
                    tenant.webqris_status === "active"
                      ? "Aktif"
                      : "Belum aktif"
                  }
                />
              </div>
              <div>
                <span>WhatsApp</span>
                <StatusBadge
                  status={tenant.wa_status ?? "not_configured"}
                  label={
                    tenant.wa_status === "active" ? "Aktif" : "Belum aktif"
                  }
                />
              </div>
            </div>
          </section>

          <section className="drawer-section">
            <p className="eyebrow">Billing & metadata</p>
            <div className="drawer-meta">
              <div>
                <span>Tipe billing</span>
                <strong>{billingLabel(tenant.billing_type)}</strong>
              </div>
              <div>
                <span>Status tenant</span>
                <StatusBadge status={tenant.status} />
              </div>
              <div>
                <span>Dibuat</span>
                <strong>{formatDateTime(tenant.created_at)}</strong>
              </div>
              <div>
                <span>Tenant ID</span>
                <strong>#{tenant.id}</strong>
              </div>
            </div>
          </section>

          <section className="drawer-section">
            <p className="eyebrow">Deposit</p>
            <div className="drawer-meta">
              <div>
                <span>Saldo deposit</span>
                <strong>{formatRupiah(ledger?.balance ?? tenant.deposit_balance)}</strong>
              </div>
              <div>
                <span>Fee terpakai</span>
                <strong>{formatRupiah(tenant.fee_total)}</strong>
              </div>
              <div>
                <span>Total top-up</span>
                <strong>{formatRupiah(tenant.topup_total)}</strong>
              </div>
            </div>

            <div className="drawer-control">
              <div className="drawer-control__head">
                <span>Tambah / koreksi saldo</span>
                <strong>Manual superadmin</strong>
              </div>
              <label className="field">
                <span>Nominal (Rp)</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  step={1000}
                  value={depositAmount}
                  onChange={(event) => setDepositAmount(event.target.value)}
                  disabled={saving !== null}
                />
              </label>
              <label className="field">
                <span>Catatan</span>
                <input
                  className="input"
                  value={depositNote}
                  onChange={(event) => setDepositNote(event.target.value)}
                  placeholder="Opsional"
                  maxLength={255}
                  disabled={saving !== null}
                />
              </label>
              <div className="card-actions">
                <button
                  type="button"
                  className="btn btn-primary btn--sm"
                  onClick={() => void adjustDeposit("credit")}
                  disabled={saving !== null}
                >
                  Tambah deposit
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn--sm"
                  onClick={() => void adjustDeposit("debit")}
                  disabled={saving !== null}
                >
                  Potong saldo
                </button>
              </div>
            </div>

            {ledger && ledger.entries.length > 0 ? (
              <div className="ledger-list">
                {ledger.entries.slice(0, 8).map((entry) => (
                  <article className="ledger-row" key={entry.id}>
                    <span
                      className={`ledger-row__icon ledger-row__icon--${entry.direction}`}
                    >
                      {entry.direction === "credit" ? (
                        <ArrowDownLeft size={15} />
                      ) : (
                        <ArrowUpRight size={15} />
                      )}
                    </span>
                    <div className="ledger-row__body">
                      <strong>{ledgerTypeLabel(entry.entryType)}</strong>
                      <span>{entry.note ?? "Tanpa catatan"}</span>
                      <small>{formatDateTime(entry.createdAt)}</small>
                    </div>
                    <div className="ledger-row__amount">
                      <strong>
                        {entry.direction === "credit" ? "+" : "-"}
                        {formatRupiah(entry.amount)}
                      </strong>
                      <span>{formatRupiah(entry.balanceAfter)}</span>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="fine-print">Belum ada mutasi deposit.</p>
            )}
          </section>

          <section className="drawer-section">
            <p className="eyebrow">Kontrol platform</p>

            <div className="drawer-control">
              <div className="drawer-control__head">
                <span>Status tenant</span>
                <strong>{statusLabel(status)}</strong>
              </div>
              <select
                className="select"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                disabled={saving !== null}
              >
                <option value="active">Aktif</option>
                <option value="grace_period">Masa tenggang</option>
                <option value="suspended">Suspend</option>
                <option value="expired">Kedaluwarsa</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
              <button
                type="button"
                className="btn btn-secondary btn-block"
                onClick={() => void saveStatus()}
                disabled={saving !== null || status === tenant.status}
              >
                {saving === "status" ? "Menyimpan..." : "Simpan status"}
              </button>
            </div>

            <div className="drawer-control">
              <div className="drawer-control__head">
                <span>Tipe billing</span>
                <strong>{billingLabel(billingType)}</strong>
              </div>
              <select
                className="select"
                value={billingType}
                onChange={(event) => setBillingType(event.target.value)}
                disabled={saving !== null}
              >
                <option value="paid">Berbayar</option>
                <option value="manual_free">Bebas manual</option>
                <option value="internal">Internal</option>
              </select>
              <button
                type="button"
                className="btn btn-secondary btn-block"
                onClick={() => void saveBilling()}
                disabled={
                  saving !== null || billingType === tenant.billing_type
                }
              >
                {saving === "billing" ? "Menyimpan..." : "Simpan billing"}
              </button>
            </div>

            <div className="drawer-control">
              <div className="drawer-control__head">
                <span>Fee rule</span>
                <strong>
                  {feeModeLabel(feeMode)} ·{" "}
                  {formatRupiah(Number(feeAmount) || 0)}
                </strong>
              </div>
              <div className="drawer-control__row">
                <label className="field">
                  <span>Mode</span>
                  <select
                    className="select"
                    value={feeMode}
                    onChange={(event) => setFeeMode(event.target.value)}
                    disabled={saving !== null}
                  >
                    <option value="per_order">Per order</option>
                    <option value="subscription">Subscription</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </label>
                <label className="field">
                  <span>Nominal (Rp)</span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={1}
                    value={feeAmount}
                    onChange={(event) => setFeeAmount(event.target.value)}
                    disabled={saving !== null}
                  />
                </label>
              </div>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={feeActive}
                  onChange={(event) => setFeeActive(event.target.checked)}
                  disabled={saving !== null}
                />
                <span>Aktifkan fee rule</span>
              </label>
              <button
                type="button"
                className="btn btn-secondary btn-block"
                onClick={() => void saveFee()}
                disabled={saving !== null || !feeChanged}
              >
                {saving === "fee" ? "Menyimpan..." : "Simpan fee"}
              </button>
            </div>
          </section>

          <div className="drawer-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void copySlug()}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Slug disalin" : "Salin slug"}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
            >
              Tutup
            </button>
          </div>

          <p className="drawer-note">
            <ShieldCheck size={15} />
            Setiap perubahan status, billing, dan fee dicatat di audit log
            platform dengan identitas superadmin Anda.
          </p>
        </div>
      </aside>
    </div>
  );
}

function PlatformBillingPage({
  tenants,
  overview,
  billing,
  onRefresh,
  onSelect,
}: {
  tenants: PlatformTenant[];
  overview: PlatformOverviewResponse | null;
  billing: PlatformBillingResponse | null;
  onRefresh: () => void;
  onSelect: (tenant: PlatformTenant) => void;
}) {
  const revenueToday = tenants.reduce(
    (total, tenant) => total + tenant.revenue_today,
    0,
  );
  const ordersToday = tenants.reduce(
    (total, tenant) => total + tenant.orders_today,
    0,
  );
  const feeConfigured = tenants.filter(
    (tenant) => tenant.fee_active === true && tenant.fee_amount !== null,
  ).length;
  const paidTenants = tenants.filter(
    (tenant) => tenant.billing_type === "paid",
  ).length;
  const feeTotal = billing?.summary.feeTotal ?? 0;
  const depositTotal = billing?.summary.depositTotal ?? 0;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Platform"
        title="Billing"
        description="Kondisi billing tenant, fee rule, dan performa pesanan hari ini."
        actions={
          <button type="button" className="btn btn-secondary" onClick={onRefresh}>
            Muat ulang
          </button>
        }
      />

      <section className="stat-grid" aria-label="Ringkasan billing">
        <article className="stat-card">
          <span className="stat-card__icon stat-card__icon--accent">
            <Wallet size={18} />
          </span>
          <div>
            <p className="stat-card__label">Omzet hari ini</p>
            <strong className="stat-card__value">
              {formatRupiah(revenueToday)}
            </strong>
            <span className="stat-card__note">Pesanan sudah dibayar</span>
          </div>
        </article>
        <article className="stat-card">
          <span className="stat-card__icon">
            <ReceiptText size={18} />
          </span>
          <div>
            <p className="stat-card__label">Pesanan hari ini</p>
            <strong className="stat-card__value">{ordersToday}</strong>
            <span className="stat-card__note">
              {overview?.counts.tenants ?? tenants.length} tenant
            </span>
          </div>
        </article>
        <article className="stat-card">
          <span className="stat-card__icon">
            <BadgeCheck size={18} />
          </span>
          <div>
            <p className="stat-card__label">Fee terkumpul</p>
            <strong className="stat-card__value">{formatRupiah(feeTotal)}</strong>
            <span className="stat-card__note">
              {feeConfigured} fee rule aktif
            </span>
          </div>
        </article>
        <article className="stat-card">
          <span className="stat-card__icon">
            <Wallet size={18} />
          </span>
          <div>
            <p className="stat-card__label">Saldo deposit</p>
            <strong className="stat-card__value">
              {formatRupiah(depositTotal)}
            </strong>
            <span className="stat-card__note">Total saldo tenant</span>
          </div>
        </article>
        <article className="stat-card">
          <span className="stat-card__icon">
            <Building2 size={18} />
          </span>
          <div>
            <p className="stat-card__label">Tenant berbayar</p>
            <strong className="stat-card__value">{paidTenants}</strong>
            <span className="stat-card__note">Tipe billing paid</span>
          </div>
        </article>
      </section>

      <section className="card card--pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Per tenant</p>
            <h2>Billing & fee</h2>
            <p className="section-subtitle">
              Klik kelola untuk mengubah status, tipe billing, dan fee rule.
            </p>
          </div>
        </div>

        {tenants.length > 0 ? (
          <div className="table-wrap">
            <table className="data-table billing-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Billing</th>
                  <th>Fee rule</th>
                  <th>Saldo</th>
                  <th>Fee terpakai</th>
                  <th>Hari ini</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td>
                      <div className="tenant-cell">
                        <span className="tenant-cell__logo">
                          {initials(tenant.name)}
                        </span>
                        <div>
                          <strong>{tenant.name}</strong>
                          <span>/{tenant.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`billing-pill billing-pill--${tenant.billing_type}`}
                      >
                        {billingLabel(tenant.billing_type)}
                      </span>
                    </td>
                    <td>
                      <div className="ops-cell">
                        <span>
                          {tenant.fee_mode
                            ? feeModeLabel(tenant.fee_mode)
                            : "Belum diatur"}
                        </span>
                        <span>
                          {tenant.fee_amount !== null
                            ? formatRupiah(tenant.fee_amount)
                            : "-"}
                          {tenant.fee_active === false ? " · nonaktif" : ""}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="ops-cell">
                        <span>{formatRupiah(tenant.deposit_balance)}</span>
                        <span>{formatRupiah(tenant.topup_total)} top-up</span>
                      </div>
                    </td>
                    <td>
                      <div className="ops-cell">
                        <span>{formatRupiah(tenant.fee_total)}</span>
                        <span>fee terkumpul</span>
                      </div>
                    </td>
                    <td>
                      <div className="ops-cell">
                        <span>{tenant.orders_today} order</span>
                        <span>{formatRupiah(tenant.revenue_today)}</span>
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={tenant.status} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn--sm"
                        onClick={() => onSelect(tenant)}
                      >
                        Kelola
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-state__icon">
              <Wallet size={24} />
            </span>
            <h3>Belum ada data billing</h3>
            <p>Billing muncul setelah tenant pertama terdaftar.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function ledgerTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    deposit: "Top-up deposit",
    fee: "Fee transaksi",
    refund: "Pengembalian fee",
    adjustment: "Koreksi saldo",
  };
  return labels[type] ?? type;
}

function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    "platform.login": "Login superadmin",
    "platform.password_reset": "Reset password superadmin",
    "platform.tenant.status": "Ubah status tenant",
    "platform.tenant.billing": "Ubah billing tenant",
    "platform.tenant.fee": "Ubah fee tenant",
    "auth.login": "Login owner",
    "auth.register": "Registrasi owner",
    "auth.password_reset": "Reset password owner",
  };
  return labels[action] ?? action;
}

function PlatformAuditPage({
  onLogout,
}: {
  onLogout: () => Promise<void>;
}) {
  const [logs, setLogs] = useState<PlatformAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (search) params.set("q", search);
      if (actionFilter) params.set("action", actionFilter);
      const response = await api<PlatformAuditResponse>(
        `/api/platform/audit?${params.toString()}`,
      );
      setLogs(response.logs);
      setError(null);
    } catch (requestError) {
      if (
        requestError instanceof ApiRequestError &&
        requestError.status === 401
      ) {
        await onLogout();
        return;
      }
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat audit log.",
      );
    } finally {
      setLoading(false);
    }
  }, [onLogout, search, actionFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Platform"
        title="Audit log"
        description="Jejak login, perubahan tenant, billing, fee, dan aktivitas platform."
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => void load()}>
            Muat ulang
          </button>
        }
      />

      <section className="card card--pad">
        <div className="audit-toolbar">
          <form
            className="search-field"
            onSubmit={(event) => {
              event.preventDefault();
              setSearch(searchInput.trim());
            }}
          >
            <Search size={16} />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Cari aksi, tenant, atau aktor..."
              aria-label="Cari audit log"
            />
          </form>
          <select
            className="select audit-action-filter"
            value={actionFilter}
            onChange={(event) => setActionFilter(event.target.value)}
            aria-label="Filter aksi audit"
          >
            <option value="">Semua aksi</option>
            <option value="platform.tenant.status">Ubah status tenant</option>
            <option value="platform.tenant.billing">Ubah billing tenant</option>
            <option value="platform.tenant.fee">Ubah fee tenant</option>
            <option value="platform.login">Login superadmin</option>
            <option value="auth.login">Login owner</option>
            <option value="auth.register">Registrasi owner</option>
            <option value="auth.password_reset">Reset password owner</option>
          </select>
        </div>

        {error ? (
          <div className="alert alert--danger" role="alert">
            <AlertTriangle size={17} />
            <span>{error}</span>
          </div>
        ) : null}

        {loading ? (
          <div className="audit-loading">
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--line" />
          </div>
        ) : logs.length > 0 ? (
          <div className="table-wrap">
            <table className="data-table audit-table">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>Aktor</th>
                  <th>Aksi</th>
                  <th>Tenant</th>
                  <th>Entitas</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="muted-cell">
                      {formatDateTime(log.created_at)}
                    </td>
                    <td>
                      <div className="owner-cell">
                        <strong>{log.actor_name ?? log.actor_type}</strong>
                        <span>{log.actor_type}</span>
                      </div>
                    </td>
                    <td>
                      <span className="audit-action">
                        {actionLabel(log.action)}
                      </span>
                      <span className="audit-action-code">{log.action}</span>
                    </td>
                    <td>{log.tenant_name ?? "-"}</td>
                    <td className="muted-cell">
                      {log.entity_type ?? "-"}
                      {log.entity_id ? ` #${log.entity_id}` : ""}
                    </td>
                    <td>
                      {log.before_data || log.after_data ? (
                        <details className="audit-detail">
                          <summary>Lihat</summary>
                          <pre>
                            {JSON.stringify(
                              {
                                before: log.before_data,
                                after: log.after_data,
                              },
                              null,
                              2,
                            )}
                          </pre>
                        </details>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-state__icon">
              <ScrollText size={24} />
            </span>
            <h3>Belum ada audit log</h3>
            <p>Log akan muncul setelah ada aktivitas platform atau tenant.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function PlatformSettingsPage({
  overview,
  tenants,
  billing,
  onRefresh,
}: {
  overview: PlatformOverviewResponse | null;
  tenants: PlatformTenant[];
  billing: PlatformBillingResponse | null;
  onRefresh: () => void;
}) {
  const origin =
    typeof window === "undefined" ? "-" : window.location.origin;
  const [apiBaseUrl, setApiBaseUrl] = useState(
    billing?.webqris?.apiBaseUrl ?? "https://webqris.com",
  );
  const [apiToken, setApiToken] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waBaseUrl, setWaBaseUrl] = useState(
    billing?.wa?.apiBaseUrl ?? "https://app.kalimashada.id",
  );
  const [waApiKey, setWaApiKey] = useState("");
  const [waSaving, setWaSaving] = useState(false);
  const [waTesting, setWaTesting] = useState(false);
  const [waMessage, setWaMessage] = useState<string | null>(null);
  const [waError, setWaError] = useState<string | null>(null);
  const [waDevice, setWaDevice] = useState<{
    name: string | null;
    sender: string | null;
    quota: number;
    status: string;
  } | null>(null);

  useEffect(() => {
    setApiBaseUrl(billing?.webqris?.apiBaseUrl ?? "https://webqris.com");
  }, [billing?.webqris?.apiBaseUrl]);

  useEffect(() => {
    setWaBaseUrl(billing?.wa?.apiBaseUrl ?? "https://app.kalimashada.id");
  }, [billing?.wa?.apiBaseUrl]);

  async function saveMerchant() {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await api("/api/platform/billing/webqris", {
        method: "PUT",
        body: JSON.stringify({
          api_base_url: apiBaseUrl.trim(),
          api_token: apiToken.trim() || undefined,
          webhook_secret: webhookSecret.trim() || undefined,
        }),
      });
      setApiToken("");
      setWebhookSecret("");
      setMessage("Konfigurasi merchant platform disimpan.");
      onRefresh();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan konfigurasi merchant platform.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function testMerchant() {
    setTesting(true);
    setMessage(null);
    setError(null);
    try {
      const result = await api<{ status: string; message: string }>(
        "/api/platform/billing/webqris/test",
        { method: "POST" },
      );
      setMessage(result.message);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menguji merchant platform.",
      );
    } finally {
      setTesting(false);
    }
  }

  async function saveWa() {
    setWaSaving(true);
    setWaMessage(null);
    setWaError(null);
    try {
      await api("/api/platform/billing/wa", {
        method: "PUT",
        body: JSON.stringify({
          api_base_url: waBaseUrl.trim(),
          api_key: waApiKey.trim() || undefined,
        }),
      });
      setWaApiKey("");
      setWaMessage("Konfigurasi WA Gateway platform disimpan.");
      onRefresh();
    } catch (requestError) {
      setWaError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan konfigurasi WA platform.",
      );
    } finally {
      setWaSaving(false);
    }
  }

  async function testWa() {
    setWaTesting(true);
    setWaMessage(null);
    setWaError(null);
    try {
      const result = await api<{
        status: string;
        message: string;
        device: {
          name: string | null;
          sender: string | null;
          quota: number;
          status: string;
        } | null;
      }>("/api/platform/billing/wa/test", { method: "POST" });
      setWaDevice(result.device);
      if (result.status === "active") {
        setWaMessage(result.message);
      } else {
        setWaError(result.message);
      }
    } catch (requestError) {
      setWaError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menguji WA Gateway platform.",
      );
    } finally {
      setWaTesting(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Platform"
        title="Pengaturan & integrasi"
        description="Konfigurasi integrasi dan ringkasan platform."
      />

      <section className="card card--pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Platform</p>
            <h2>WebQRIS</h2>
          </div>
          <Wallet size={20} className="muted-icon" />
        </div>

        {message ? (
          <div className="alert alert--success" role="status">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="alert alert--danger" role="alert">
            {error}
          </div>
        ) : null}

        <div className="webhook-panel">
          <div>
            <span className="webhook-panel__label">Webhook URL</span>
            <code title={billing?.webqris?.webhookUrl ?? `${origin}/api/webhooks/webqris/platform`}>
              {billing?.webqris?.webhookUrl ?? `${origin}/api/webhooks/webqris/platform`}
            </code>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn--sm"
            onClick={() => {
              const url =
                billing?.webqris?.webhookUrl ??
                `${origin}/api/webhooks/webqris/platform`;
              void navigator.clipboard?.writeText(url);
            }}
          >
            <Copy size={14} /> Salin
          </button>
        </div>

        <div className="form-grid">
          <label className="field field--full">
            <span>API base URL</span>
            <input
              className="input"
              value={apiBaseUrl}
              onChange={(event) => setApiBaseUrl(event.target.value)}
              placeholder="https://webqris.com"
            />
          </label>
          <label className="field">
            <span>API token</span>
            <input
              className="input"
              type="password"
              value={apiToken}
              onChange={(event) => setApiToken(event.target.value)}
              placeholder={
                billing?.webqris?.hasApiToken
                  ? "Sudah tersimpan — isi untuk mengganti"
                  : "Tempel API token"
              }
              autoComplete="new-password"
            />
          </label>
          <label className="field">
            <span>Webhook secret</span>
            <input
              className="input"
              type="password"
              value={webhookSecret}
              onChange={(event) => setWebhookSecret(event.target.value)}
              placeholder={
                billing?.webqris?.hasWebhookSecret
                  ? "Sudah tersimpan — isi untuk mengganti"
                  : "Tempel webhook secret"
              }
              autoComplete="new-password"
            />
          </label>
        </div>
        <div className="card-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void saveMerchant()}
            disabled={saving}
          >
            <Check size={16} /> {saving ? "Menyimpan..." : "Simpan WebQRIS"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void testMerchant()}
            disabled={testing || !billing?.webqris?.hasApiToken}
          >
            {testing ? <span className="spin" /> : <Check size={16} />}
            Tes koneksi
          </button>
        </div>
      </section>

      <section className="card card--pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Platform</p>
            <h2>WhatsApp Gateway</h2>
          </div>
          <ShieldCheck size={20} className="muted-icon" />
        </div>

        {waMessage ? (
          <div className="alert alert--success" role="status">
            {waMessage}
          </div>
        ) : null}
        {waError ? (
          <div className="alert alert--danger" role="alert">
            {waError}
          </div>
        ) : null}

        <div className="form-grid">
          <label className="field field--full">
            <span>API base URL</span>
            <input
              className="input"
              value={waBaseUrl}
              onChange={(event) => setWaBaseUrl(event.target.value)}
              placeholder="https://app.kalimashada.id"
            />
          </label>
          <label className="field field--full">
            <span>API key</span>
            <input
              className="input"
              type="password"
              value={waApiKey}
              onChange={(event) => setWaApiKey(event.target.value)}
              placeholder={
                billing?.wa?.hasApiKey
                  ? "Sudah tersimpan — isi untuk mengganti"
                  : "Tempel API key device WA platform"
              }
              autoComplete="new-password"
            />
          </label>
        </div>
        <div className="card-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void saveWa()}
            disabled={waSaving}
          >
            <Check size={16} /> {waSaving ? "Menyimpan..." : "Simpan WA"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void testWa()}
            disabled={waTesting || !billing?.wa?.hasApiKey}
          >
            {waTesting ? <span className="spin" /> : <Check size={16} />}
            Tes koneksi
          </button>
        </div>
        {waDevice ? (
          <div className="integration-meta">
            <span>
              Device: <strong>{waDevice.name ?? "-"}</strong>
            </span>
            <span>
              Pengirim: <strong>{waDevice.sender ?? "-"}</strong>
            </span>
            <span>
              Status: <strong>{waDevice.status}</strong>
            </span>
            <span>
              Kuota: <strong>{waDevice.quota}</strong>
            </span>
          </div>
        ) : null}
      </section>

      <section className="card card--pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Kapasitas</p>
            <h2>Ringkasan platform</h2>
          </div>
        </div>
        <div className="platform-metrics">
          <div>
            <span>Tenant</span>
            <strong>{overview?.counts.tenants ?? tenants.length}</strong>
          </div>
          <div>
            <span>Tenant aktif</span>
            <strong>{overview?.counts.activeTenants ?? 0}</strong>
          </div>
          <div>
            <span>Outlet</span>
            <strong>{overview?.counts.outlets ?? 0}</strong>
          </div>
          <div>
            <span>Meja/kamar</span>
            <strong>{overview?.counts.units ?? 0}</strong>
          </div>
        </div>
      </section>
    </div>
  );
}

type OwnerDraft = {
  tenantName: string;
  businessType: "cafe" | "kost";
  ownerName: string;
  username: string;
  phone: string;
  email: string;
  password: string;
  outletName: string;
  billingType: "paid" | "manual_free" | "internal";
  feeAmount: string;
};

const emptyOwnerDraft: OwnerDraft = {
  tenantName: "",
  businessType: "cafe",
  ownerName: "",
  username: "",
  phone: "",
  email: "",
  password: "",
  outletName: "",
  billingType: "paid",
  feeAmount: "100",
};

function CreateOwnerSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (tenantName: string) => void;
}) {
  const [draft, setDraft] = useState<OwnerDraft>(emptyOwnerDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<Key extends keyof OwnerDraft>(
    key: Key,
    value: OwnerDraft[Key],
  ) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function generatePassword() {
    const alphabet =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
    const values = crypto.getRandomValues(new Uint32Array(14));
    const password = Array.from(values)
      .map((value) => alphabet[value % alphabet.length])
      .join("");
    update("password", `${password}!`);
  }

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await api("/api/platform/tenants", {
        method: "POST",
        body: JSON.stringify({
          tenant_name: draft.tenantName.trim(),
          business_type: draft.businessType,
          owner_name: draft.ownerName.trim(),
          username: draft.username.trim(),
          phone: draft.phone.trim(),
          email: draft.email.trim() || undefined,
          password: draft.password,
          outlet_name: draft.outletName.trim() || undefined,
          billing_type: draft.billingType,
          fee_amount: Number(draft.feeAmount || 0),
        }),
      });
      onCreated(draft.tenantName.trim());
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal membuat owner.",
      );
    } finally {
      setSaving(false);
    }
  }

  const valid =
    draft.tenantName.trim().length >= 2 &&
    draft.ownerName.trim().length >= 2 &&
    draft.username.trim().length >= 3 &&
    draft.phone.trim().length >= 8 &&
    draft.password.length >= 8;

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sheet sheet--form"
        role="dialog"
        aria-modal="true"
        aria-label="Tambah owner"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet__head">
          <div>
            <p className="eyebrow">Platform</p>
            <h2>Tambah owner</h2>
            <p className="section-subtitle">
              Buat tenant, akun owner, dan outlet pertama dalam satu langkah.
            </p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="sheet__body">
          {error ? (
            <div className="alert alert--danger" role="alert">
              <AlertTriangle size={17} />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="form-grid">
            <label className="field">
              <span>Nama tenant</span>
              <input
                className="input"
                autoFocus
                value={draft.tenantName}
                onChange={(event) => update("tenantName", event.target.value)}
                placeholder="Nama cafe / kost"
              />
            </label>
            <label className="field">
              <span>Jenis usaha</span>
              <select
                className="input"
                value={draft.businessType}
                onChange={(event) =>
                  update("businessType", event.target.value as "cafe" | "kost")
                }
              >
                <option value="cafe">Cafe / Restoran</option>
                <option value="kost">Kost / Hunian</option>
              </select>
            </label>
            <label className="field">
              <span>Nama owner</span>
              <input
                className="input"
                value={draft.ownerName}
                onChange={(event) => update("ownerName", event.target.value)}
              />
            </label>
            <label className="field">
              <span>Username owner</span>
              <input
                className="input"
                autoCapitalize="none"
                value={draft.username}
                onChange={(event) => update("username", event.target.value)}
                placeholder="owner-usaha"
              />
            </label>
            <label className="field">
              <span>WhatsApp owner</span>
              <input
                className="input"
                inputMode="tel"
                value={draft.phone}
                onChange={(event) => update("phone", event.target.value)}
                placeholder="08xxxxxxxxxx"
              />
            </label>
            <label className="field">
              <span>Email owner (opsional)</span>
              <input
                className="input"
                type="email"
                value={draft.email}
                onChange={(event) => update("email", event.target.value)}
              />
            </label>
            <label className="field">
              <span>Nama outlet pertama</span>
              <input
                className="input"
                value={draft.outletName}
                onChange={(event) => update("outletName", event.target.value)}
                placeholder="Default: nama tenant"
              />
            </label>
            <label className="field">
              <span>Tipe billing</span>
              <select
                className="input"
                value={draft.billingType}
                onChange={(event) =>
                  update(
                    "billingType",
                    event.target.value as OwnerDraft["billingType"],
                  )
                }
              >
                <option value="paid">Berbayar (fee per transaksi)</option>
                <option value="manual_free">Bebas manual</option>
                <option value="internal">Internal</option>
              </select>
            </label>
            <label className="field">
              <span>Fee per order (Rp)</span>
              <input
                className="input"
                type="number"
                min={0}
                value={draft.feeAmount}
                onChange={(event) => update("feeAmount", event.target.value)}
              />
            </label>
            <label className="field field--full">
              <span>Password awal owner</span>
              <span className="input-group">
                <input
                  className="input"
                  type="text"
                  value={draft.password}
                  onChange={(event) => update("password", event.target.value)}
                  minLength={8}
                  placeholder="Minimal 8 karakter"
                />
                <button
                  type="button"
                  className="input-group__action"
                  onClick={generatePassword}
                  title="Buat password acak"
                >
                  <KeyRound size={16} />
                </button>
              </span>
            </label>
          </div>

          <p className="fine-print">
            Simpan password awal dan berikan ke owner lewat jalur aman. Owner
            dapat menggantinya dari menu Profil. Reset password owner juga bisa
            dikirim sebagai OTP WhatsApp dari detail tenant.
          </p>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Batal
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void submit()}
              disabled={saving || !valid}
            >
              <UserPlus size={16} /> {saving ? "Membuat..." : "Buat owner"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlatformProfilePage({
  admin,
  onUpdated,
}: {
  admin: PlatformAdmin;
  onUpdated: (admin: PlatformAdmin) => void;
}) {
  const [name, setName] = useState(admin.name);
  const [phone, setPhone] = useState(admin.phone ?? "");
  const [email, setEmail] = useState(admin.email ?? "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(admin.name);
    setPhone(admin.phone ?? "");
    setEmail(admin.email ?? "");
  }, [admin.email, admin.name, admin.phone]);

  async function saveProfile() {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api<{ admin: PlatformAdmin }>(
        "/api/platform/auth/profile",
        {
          method: "PATCH",
          body: JSON.stringify({
            name: name.trim(),
            phone: phone.trim() || null,
            email: email.trim() || null,
          }),
        },
      );
      onUpdated(response.admin);
      setMessage("Profil superadmin diperbarui.");
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan profil.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function changePassword() {
    setError(null);
    setMessage(null);
    if (newPassword.length < 8) {
      setError("Password baru minimal 8 karakter.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Konfirmasi password baru belum sama.");
      return;
    }
    setChanging(true);
    try {
      await api("/api/platform/auth/profile/password", {
        method: "POST",
        body: JSON.stringify({
          new_password: newPassword,
        }),
      });
      setNewPassword("");
      setConfirmPassword("");
      setMessage("Password superadmin berhasil diubah.");
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal mengubah password.",
      );
    } finally {
      setChanging(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Platform"
        title="Profil superadmin"
        description="Identitas platform dan keamanan akun superadmin. Akun ini tidak terikat pada tenant mana pun."
      />

      {error ? (
        <div className="alert alert--danger" role="alert">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="alert alert--success" role="status">
          {message}
        </div>
      ) : null}

      <div className="overview-grid">
        <section className="card card--pad">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Akun</p>
              <h2>Identitas superadmin</h2>
            </div>
            <ShieldCheck size={18} className="muted-icon" />
          </div>
          <div className="form-grid">
            <label className="field field--full">
              <span>Nama</span>
              <input
                className="input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                minLength={2}
                maxLength={120}
              />
            </label>
            <label className="field">
              <span>Username</span>
              <input className="input" value={`@${admin.username}`} disabled />
            </label>
            <label className="field">
              <span>Realm</span>
              <input className="input" value="Platform / Superadmin" disabled />
            </label>
            <label className="field">
              <span>Nomor WhatsApp</span>
              <input
                className="input"
                inputMode="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </label>
            <label className="field">
              <span>Email (opsional)</span>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
          </div>
          <div className="card-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void saveProfile()}
              disabled={saving || name.trim().length < 2}
            >
              <Check size={16} /> {saving ? "Menyimpan..." : "Simpan profil"}
            </button>
          </div>
        </section>

        <aside className="card card--pad">
          <div className="section-heading section-heading--compact">
            <div>
              <p className="eyebrow">Keamanan</p>
              <h2>Ganti password</h2>
            </div>
            <KeyRound size={18} className="muted-icon" />
          </div>
          <div className="form-grid">
            <label className="field">
              <span>Password baru</span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                minLength={8}
              />
            </label>
            <label className="field">
              <span>Ulangi password baru</span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={8}
              />
            </label>
          </div>
          <div className="card-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void changePassword()}
              disabled={
                changing ||
                newPassword.length < 8 ||
                newPassword !== confirmPassword
              }
            >
              <KeyRound size={16} />
              {changing ? "Mengubah..." : "Ubah password"}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PlatformPlaceholder({ tab }: { tab: string }) {
  const config = {
    billing: {
      title: "Billing platform",
      description:
        "Fee per order, deposit, invoice subscription, dan revenue platform.",
      icon: Wallet,
    },
    audit: {
      title: "Audit log",
      description:
        "Jejak aksi superadmin, perubahan tenant, dan aktivitas integrasi.",
      icon: ScrollText,
    },
    settings: {
      title: "Pengaturan platform",
      description:
        "Pricing default, merchant top-up platform, dan feature flag global.",
      icon: Settings,
    },
  }[tab] ?? {
    title: "Modul platform",
    description: "Modul ini akan tersedia pada fase berikutnya.",
    icon: ShieldCheck,
  };

  const Icon = config.icon;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Platform"
        title={config.title}
        description={config.description}
      />
      <section className="card card--pad">
        <div className="empty-state empty-state--roomy">
          <span className="empty-state__icon empty-state__icon--large">
            <Icon size={28} />
          </span>
          <span className="mini-pill">Modul berikutnya</span>
          <h3>{config.title} belum diaktifkan</h3>
          <p>
            Fondasi multi-tenant, autentikasi superadmin, dan daftar tenant
            sudah berjalan. Modul ini mengikuti roadmap blueprint.
          </p>
          <Link className="btn btn-primary" to="/platform/tenants">
            Kembali ke tenant
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
